/*
 * @Author: Teemo Ding
 * @Date: 2021-06-15 15:05:47
 * @LastEditors: Teemo Ding
 * @LastEditTime: 2021-10-15 11:40:55
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesChannelProductsConfig/SalesChannelProductAlias/EditPage/index.tsx
 */

import React, { useState } from 'react';

import { LeftOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { useSalesChannelType } from 'src/constrains/SalesChannelHOC/useSalesChannel';
import BasicPageHOC from 'src/constrains/SalesChannelPreviewPageComponents/BasicPageHOC';
import PageWrapper from 'src/constrains/SalesChannelPreviewPageComponents/PageWrapper/PageWrapper2';
import RightConfigWrapper from 'src/constrains/SalesChannelPreviewPageComponents/RightConfigWrapper';
import RouteBlock from 'src/pages/SalesChannelContentEditor/SalesChannelEditorHomepage/RouteBlock';
import { dispatch } from 'src/store';
import { ChannelType } from 'src/types/common/SalesChannel';
import Carousel from './Carousel';
import CloneBtn from './CloneButton';
import ContentDetails from './ContentDetails';
import MiniProgramInfo from './MiniInfo';
import MiniProgramShare from './MiniShare';
import Preview from './Preview';
import SEOSetting from './SEOSetting';
import ThumbnailImage from './ThumbnailImage';
import { ProductType } from './common';
import { aliasIdHooks } from './hooks/aliasIdHooks';
import { convertDataFromReduxToServer } from './tools';

import {
  getAliasUuid,
  getContent,
  getContentDetails,
  getHasModified,
  getSalesChannel,
  getSalesChannelName,
  getState,
  initAliasDetail,
  initNewAlias,
  onAddAlias,
  submitAlias,
  validation,
} from './slice';

import {
  isUserValid,
  salesChannelResourceNameGenerator,
} from 'src/tools/getUserInfo';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from 'src/types/common/resources';

import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import styles from './style.module.less';

const permissions = (saleChannelUuid) => {
  const canViewDetails = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        uuid: saleChannelUuid,
        category: EnumCategory.SalesChannelProduct,
        subCategory: EnumSubCategory.ChannelProduct,
        operation: EnumOperation.view_product_details_page,
      }),
    ],
  });
  const canCreate = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        uuid: saleChannelUuid,
        category: EnumCategory.SalesChannelProduct,
        subCategory: EnumSubCategory.ChannelProduct,
        operation: EnumOperation.add_edit_product,
      }),
    ],
  });
  return { canViewDetails, canCreate };
};

const SalesChannelProductAliasEdit = (props) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const [loaded] = useState(true);
  const {
    onDiscard,
    onGoBack,
    submitData,
    hasModified,
    salesChannelName,
    isOnPrimaryLanguage,
  } = props;

  const aliasData = useSelector(getContentDetails());
  const productType = useSelector(getContent('productType'));
  const productName = useSelector(getContent('productName'));
  const type = useSalesChannelType();
  const _params: {
    salesChannelProductUuid: string;
    saleChannelUuid: string;
    aliasUuid: string;
  } = useParams();
  const history = useHistory();
  const { salesChannelProductUuid, saleChannelUuid } = _params;

  aliasIdHooks(getAliasUuid);
  const wrapperTitle = (
    <div
      onClick={() =>
        history.push(
          `/salesChannel/products/alias/${saleChannelUuid}/${salesChannelProductUuid}`,
        )
      }>
      <LeftOutlined />
      {aliasData.productName}
    </div>
  );

  const { canViewDetails, canCreate } = permissions(saleChannelUuid);

  const child = [
    {
      body: (
        <div>
          {!isOnPrimaryLanguage && canCreate && <CloneBtn />}
          <ContentDetails
            isOnPrimaryLanguage={isOnPrimaryLanguage}
            disabled={!canCreate}
          />
          <Carousel disabled={!canCreate} />
          {(productType == ProductType.MultiplePhysical ||
            productType == ProductType.MultipleVirtual) && (
            <ThumbnailImage
              isOnPrimaryLanguage={isOnPrimaryLanguage}
              disabled={!canCreate}
            />
          )}
          {type !== ChannelType.MiniProgram && (
            <SEOSetting
              isOnPrimaryLanguage={isOnPrimaryLanguage}
              disabled={!canCreate}
            />
          )}
          {type == ChannelType.MiniProgram && (
            <MiniProgramShare disabled={!canCreate} />
          )}
          {type == ChannelType.MiniProgram && <MiniProgramInfo />}
        </div>
      ),
      key: 'TopConfig',
    },
  ];

  return (
    <div className={styles.container}>
      <React.Fragment>
        <RouteBlock getHasModified={getHasModified} />
        <PageWrapper
          headerConfig={{
            description: (canViewDetails && productName) || '',
            topTitle: salesChannelName,
            hasModified,
            submit: canCreate && submitData,
            // salesChannelContentData: salesChannel || {},
            // salesChannelName,
            onDiscard,
            onGoBack,
          }}
          leftChild={
            canViewDetails ? (
              <div className={styles.left}>
                <Preview />
              </div>
            ) : (
              <div className={styles.content}>
                {getI18n('general.permission.permissiondenied')}
              </div>
            )
          }
          rightChild={
            loaded && canViewDetails ? (
              <RightConfigWrapper title={wrapperTitle} elements={child} />
            ) : null
          }
        />
      </React.Fragment>
    </div>
  );
};
export default BasicPageHOC(SalesChannelProductAliasEdit, {
  getState,
  convertDataFromReduxToServer,
  submit: (o) => {
    if (o.urlParams.aliasUuid === 'new') {
      return onAddAlias(o);
    } else {
      return submitAlias(o);
    }
  },
  getHasModified,
  getSalesChannel,
  getSalesChannelName,
  validation,
  queryData: (params, qParams, history) => {
    const { salesChannelProductUuid, aliasUuid } = params;
    if (aliasUuid && aliasUuid !== 'new') {
      dispatch(initAliasDetail({ body: aliasUuid, params: qParams }));
    } else {
      dispatch(
        initNewAlias({ body: salesChannelProductUuid, params: qParams }),
      );
    }
  },
});
